import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useCatalog } from '@/context/CatalogContext';
import { formatRSD } from '@/lib/currency';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const pathCategoryMap: Record<string, string> = {
  '/kucni-brojevi':    'house',
  '/plocice-za-vrata': 'door',
  '/plocice-za-sanuce':'mailbox',
  '/znakovi':          'dog',
};

export default function ShopPage() {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { products, categories } = useCatalog();
  const urlCategory = searchParams.get('category') || pathCategoryMap[pathname] || null;
  const [activeCategory, setActiveCategory] = useState(urlCategory || 'all');
  const [addedId, setAddedId] = useState<string | null>(null);
  const { addItem } = useCart();
  const sectionRef = useRef<HTMLDivElement>(null);

  const categoryFilter = [
    { slug: 'all', name: 'Svi proizvodi', count: products.length },
    ...categories.map((c) => ({ slug: c.slug, name: c.name, count: c.count })),
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal');
            els.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-in');
              }, i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [products.length]);

  // Update active category when URL changes
  useEffect(() => {
    if (urlCategory) {
      setActiveCategory(urlCategory);
    }
  }, [urlCategory]);

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter((p) => p.category === activeCategory);

  const handleAdd = (product: (typeof products)[number]) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      oldPrice: product.oldPrice ?? undefined,
      image: product.image,
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="w-full px-4 md:px-6 py-6 md:py-10 max-w-[1450px] mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-foreground-600 mb-6">
          <Link to="/" className="hover:text-amber-600 transition-colors">Početna</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-950">Proizvodi</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-2">
            Proizvodi
          </h1>
          <p className="text-base text-foreground-700">
            Pronađite savršen kućni broj, pločicu ili oznaku po meri. Izaberite iz naših kolekcija.
          </p>
        </div>

        {/* Configurator banner hidden for now - see router/config.tsx to re-enable */}

        <div className="flex flex-col lg:flex-row gap-6 md:gap-8" ref={sectionRef}>
          {/* Sidebar filters */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <h3 className="font-label text-sm font-semibold text-foreground-950 mb-4 uppercase tracking-wider">
                Kategorije
              </h3>
              <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
                {categoryFilter.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`flex items-center justify-between px-4 py-2.5 rounded-lg font-label text-sm whitespace-nowrap transition-all flex-shrink-0 lg:w-full ${
                      activeCategory === cat.slug
                        ? 'bg-foreground-950 text-white'
                        : 'bg-white text-foreground-700 hover:bg-background-100 border border-background-200'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className={`text-xs font-medium ml-2 ${activeCategory === cat.slug ? 'text-white/70' : 'text-foreground-500'}`}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Products grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-foreground-600">
                Prikazano {filteredProducts.length} proizvoda
              </span>
              <div className="flex items-center gap-2 text-sm text-foreground-700">
                <i className="ri-sort-desc" />
                <span className="font-label">Sortiraj</span>
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="text-center py-16 text-foreground-600 text-sm">
                Trenutno nema proizvoda u ovoj kategoriji.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out bg-white rounded-lg border border-background-200 overflow-hidden hover:border-amber-300 hover:shadow-sm transition-all duration-300 group"
                  >
                    <Link to={`/product/${product.id}`} className="block relative aspect-square overflow-hidden bg-background-100">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`${product.name} - Kućni brojevi po meri`}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      {product.badge && (
                        <div className="absolute top-3 left-3 px-2.5 py-1 bg-amber-600 text-white text-[10px] font-label font-medium rounded-sm uppercase tracking-wider">
                          {product.badge}
                        </div>
                      )}
                    </Link>
                    <div className="p-4">
                      <Link to={`/product/${product.id}`} className="block">
                        <h3 className="font-label text-sm font-medium text-foreground-950 mb-2 line-clamp-1 hover:text-amber-600 transition-colors">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-3 h-3 flex items-center justify-center">
                            <i className={`${i < product.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-background-400'} text-[10px]`} />
                          </div>
                        ))}
                        {product.reviews > 0 && (
                          <span className="text-[10px] text-foreground-600 ml-1">({product.reviews})</span>
                        )}
                      </div>
                      <div className="flex items-baseline gap-2 mb-3">
                        <span className="font-label text-base font-bold text-foreground-950">
                          {formatRSD(product.price)}
                        </span>
                        {product.oldPrice && (
                          <span className="font-label text-xs text-foreground-500 line-through">
                            {formatRSD(product.oldPrice)}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleAdd(product)}
                        className={`w-full py-2.5 font-label text-sm font-medium rounded-md transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
                          addedId === product.id
                            ? 'bg-green-600 text-white'
                            : 'bg-foreground-950 text-white hover:bg-foreground-900'
                        }`}
                      >
                        <i className={addedId === product.id ? 'ri-check-line text-xs' : 'ri-shopping-cart-line text-xs'} />
                        {addedId === product.id ? 'Dodato' : 'Dodaj u korpu'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />

      <style>{`
        .reveal.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}
